import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import React from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner/InfoBanner";
import { WarningOutlineIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import {
  PackageBenefitsSummary,
  useCancelMemberPackage,
  usePackageDisplayName,
} from "@whoosh/features/src/Packages";

import { CancelPackageModal_memberPackage$key } from "./__generated__/CancelPackageModal_memberPackage.graphql";

type Props = {
  memberPackageRef: CancelPackageModal_memberPackage$key | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  onCancelSuccess: () => void;
  onCancelError?: (error: Error) => void;
};

export const CancelPackageModal = ({
  isOpen,
  onClose,
  onCancelSuccess,
  onCancelError,
  memberPackageRef,
}: Props) => {
  const memberPackage = useFragment(
    CancelPackageModalFragmentString,
    memberPackageRef
  );

  const [cancelMemberPackage, isCancelingMemberPackage] =
    useCancelMemberPackage();
  const packageName = usePackageDisplayName(memberPackage?.package);

  const onCancelPackage = () => {
    if (memberPackage?.id) {
      cancelMemberPackage(
        { id: memberPackage.id },
        onCancelSuccess,
        onCancelError
      );
    }
  };

  const hasBenefits = (memberPackage?.package.benefitsSummary || []).length > 0;

  return (
    <StandardModal
      isOpen={isOpen}
      upperTitle={t`Cancel Subscription`}
      content={
        <VStack px={6} gap={4}>
          <Text color="text-secondary">
            {t`We are sorry to see you go. Are you sure you want to cancel your subscription to `}
            <Text>{packageName}</Text>
            <Text color="text-secondary">?</Text>
          </Text>
          <InfoBanner
            leadingIcon={WarningOutlineIcon}
            message={t`You will lose access to all benefits at the end of the current billing period. All scheduled payments until then will be processed.`}
            colorTheme="ERROR"
          />
          {hasBenefits ? (
            <Text variant="mainHeading">{t`You will lose access to:`}</Text>
          ) : null}
          <PackageBenefitsSummary
            packageRef={memberPackage?.package}
            hideTitle
          />
        </VStack>
      }
      onClose={onClose}
      footerContent={
        <HStack gap={2} justifyContent="flex-end" pb={6}>
          <Button
            accessibilityLabel={t`Dismiss`}
            variant="secondary"
            size="small"
            onPress={onClose}
          >{t`Dismiss`}</Button>
          <Button
            accessibilityLabel={t`Cancel Subscription`}
            variant="danger"
            size="small"
            onPress={onCancelPackage}
            isLoading={isCancelingMemberPackage}
          >{t`Cancel Subscription`}</Button>
        </HStack>
      }
    />
  );
};

const CancelPackageModalFragmentString = graphql`
  fragment CancelPackageModal_memberPackage on MemberPackage {
    id
    package {
      ...usePackageDisplayName_package
      ...PackageBenefitsSummary_package
      benefitsSummary {
        description
      }
    }
  }
`;
